"use client";

import { useCallback, useRef, useState } from "react";
import { Check, LinkSimple, ShareNetwork } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface IShareButtonProps {
  url: string;
  title: string;
  className?: string;
  /** `default` shows icon + label; `icon` is icon-only for inline card use. */
  variant?: "default" | "icon";
}

function shareUrl(
  platform: "x" | "facebook" | "linkedin",
  url: string,
  title: string
): string {
  const encoded = encodeURIComponent(url);
  const text = encodeURIComponent(title);
  switch (platform) {
    case "x":
      return `https://x.com/intent/tweet?url=${encoded}&text=${text}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encoded}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`;
  }
}

const SHARE_ITEMS = [
  { platform: "x" as const, label: "X" },
  { platform: "facebook" as const, label: "Facebook" },
  { platform: "linkedin" as const, label: "LinkedIn" },
];

export function ShareButton({
  url,
  title,
  className,
  variant = "default",
}: IShareButtonProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  function handleMouseEnter(): void {
    clearCloseTimeout();
    setOpen(true);
  }

  function handleMouseLeave(): void {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => setOpen(false), 150);
  }

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={variant === "icon" ? "icon" : "sm"}
          className={cn(
            variant === "icon" && "h-8 w-8 shrink-0",
            variant === "default" && "gap-1.5",
            className
          )}
          aria-label="Share"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ShareNetwork className="h-4 w-4" aria-hidden="true" />
          {variant === "default" && <span>Share</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-44 p-1"
        align="end"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ul className="flex flex-col">
          {SHARE_ITEMS.map(({ platform, label }) => (
            <li key={platform}>
              <a
                href={shareUrl(platform, url, title)}
                target="_blank"
                rel="noreferrer noopener"
                className="flex w-full rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                {label}
              </a>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={() => void copyLink()}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              {copied ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : (
                <LinkSimple className="h-4 w-4" aria-hidden="true" />
              )}
              {copied ? "Copied!" : "Copy link"}
            </button>
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}
